import { jsxs, jsx } from 'react/jsx-runtime';
import { size } from '@floating-ui/react';
import { motion, useWillChange, useMotionValue, animate, AnimatePresence } from 'framer-motion';
import { useState, useCallback } from 'react';
import { styled, css } from 'styled-components';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { FloatAnchor } from '@fabric-app/ui/components/FloatAnchor';

const FloatingText = styled(motion.div).withConfig({
  displayName: "useAnimatedText__FloatingText",
  componentId: "sc-88e33de7-0"
})([
  `z-index:127;display:inline-block;> span{display:inline-flex;transform-origin:center;letter-spacing:-0.001em;}color:`,
  `;font-weight:500;`,
  ``
], cssVar["color-text-primary"], ({ overflows }) => overflows && css([
  `white-space:pre-wrap;word-break:break-all;line-break:anywhere;overflow-wrap:break-word;`
]));
const useAnimatedText = (options) => {
  const { updateRealText, floatingTextStyle, overflows } = options;
  const willChange = useWillChange();
  const animatedPosition = useMotionValue(0);
  const [animatedText, setAnimatedText] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const onAnimateText = useCallback((newText) => {
    animatedPosition.set(0);
    setAnimatedText("");
    animate(animatedPosition, newText.length, {
      duration: newText.length / 50,
      onPlay() {
        setIsAnimating(true);
      },
      onUpdate(latest) {
        const updateText = newText.substring(0, Math.round(latest));
        setAnimatedText(updateText);
        updateRealText(updateText);
      },
      onComplete() {
        setIsAnimating(false);
        updateRealText(newText);
      }
    });
  }, [
    animatedPosition,
    setAnimatedText,
    updateRealText,
    setIsAnimating
  ]);
  return {
    isAnimating,
    onAnimateText,
    inputStyle: isAnimating ? {
      color: "#00000000"
    } : {},
    addAnimation: (children, disabled = false) => disabled ? children : /* @__PURE__ */ jsxs(FloatAnchor.Root, {
      // since we just want it on top of the current element there is no need to use transform
      // and using transform causes blurry text as it shows up
      useTransform: false,
      offset: ({ rects }) => {
        if (rects.floating.width - 15 > rects.reference.width) setIsAnimating(false);
        return overflows ? -rects.reference.height : -rects.reference.height / 2 - rects.floating.height / 2;
      },
      middleware: overflows ? [
        size({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              maxWidth: `${rects.reference.width}px`,
              height: `${rects.reference.height}px`
            });
          }
        })
      ] : void 0,
      open: isAnimating,
      children: [
        /* @__PURE__ */ jsx(FloatAnchor.Trigger, {
          action: "manual",
          asChild: true,
          children
        }),
        /* @__PURE__ */ jsx(FloatAnchor.Floating, {
          asChild: true,
          children: /* @__PURE__ */ jsx(FloatingText, {
            style: floatingTextStyle,
            overflows,
            children: /* @__PURE__ */ jsx(AnimatePresence, {
              children: animatedText.split("").map((c, i) => /* @__PURE__ */ jsx(Letter, {
                initial: {
                  transform: "translateY(7px)",
                  opacity: 0,
                  filter: "blur(2px)"
                },
                animate: {
                  transform: "translateY(0)",
                  opacity: 1,
                  filter: "blur(0)",
                  // After the transition ends we revert back to inline otherwise
                  // it will have a slightly different text size which feels abrupt when
                  // handing it back to the input.
                  transitionEnd: {
                    display: "inline"
                  }
                },
                style: {
                  willChange
                },
                children: c === " " ? " " : c
              }, i))
            })
          })
        })
      ]
    })
  };
};
const Letter = styled(motion.span).withConfig({
  displayName: "useAnimatedText__Letter",
  componentId: "sc-88e33de7-1"
})([
  `font-family:inherit;`
]);

export { useAnimatedText };
//# sourceMappingURL=useAnimatedText.js.map
